import React from 'react';
import { CircleCheck as CheckCircle, CloudLightning, Loader as Loader2, RefreshCw } from 'lucide-react';
import type { CoreInfo } from '../types';

interface WpUpdatesTabProps {
  coreInfo: CoreInfo | null;
  coreHasUpdate: boolean;
  isSyncing: boolean;
  errorCore: string | null;
  actionLoading: string | null;
  onCoreUpdate: () => void;
}

export const WpUpdatesTab: React.FC<WpUpdatesTabProps> = ({
  coreInfo,
  coreHasUpdate,
  isSyncing,
  errorCore,
  actionLoading,
  onCoreUpdate,
}) => {
  if (isSyncing && !coreInfo) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (errorCore) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-sm text-muted-foreground">Failed to load: {errorCore}</p>
      </div>
    );
  }

  if (!coreHasUpdate) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center mb-3">
          <CheckCircle className="w-6 h-6 text-emerald-500" />
        </div>
        <p className="text-sm font-medium text-foreground">WordPress Core is up to date</p>
        <p className="text-xs text-muted-foreground mt-1">Currently running version {coreInfo?.wordpress?.version}</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40 rounded-xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-2.5 rounded-xl bg-white dark:bg-blue-900/40 shadow-sm">
            <CloudLightning className="w-6 h-6 text-blue-500" />
          </div>
          <div>
            <p className="text-sm font-bold text-foreground">WordPress Update Available</p>
            <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
              v{coreInfo?.wordpress?.version} → <span className="font-bold">v{coreInfo?.wordpress?.new_version}</span>
            </p>
          </div>
        </div>
        <button
          onClick={onCoreUpdate}
          disabled={!!actionLoading}
          className="flex items-center justify-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold shadow-lg shadow-blue-500/20 transition-all active:scale-95 disabled:opacity-50"
        >
          {actionLoading === 'core-update' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Update WordPress Now
        </button>
      </div>
    </div>
  );
};
